// The whole tutorial behind the `editor-tutorial` Unleash flag: the «Помощь»
// item, the ⌘/ hub link, the `/tutorial` route, the offer card and its resume
// form, and the runner all ask this one gate.
//
// `?tutorial` on any page turns the tutorial on for the browser session — the
// local Unleash proxy is unreachable, so dev and QA have no other way in. The
// override only adds: it never turns the flag off for someone who has it.

import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { tutorialOverrideAtom } from '@/atoms/tutorial';

/** The Unleash flag the tutorial rolls out behind. */
export const TUTORIAL_FLAG = 'editor-tutorial';

/** The dev/QA override: present in the query, with any value or none. */
export const hasTutorialParam = (search: string) => new URLSearchParams(search).has('tutorial');

export interface TutorialGateInput {
    flagOn: boolean;
    /** `?tutorial` is in the URL now. */
    param: boolean;
    /** `?tutorial` was seen earlier in this browser session. */
    sticky: boolean;
}

/** `stick`: the override was just seen and the session should keep it. */
export function decideTutorialGate({ flagOn, param, sticky }: TutorialGateInput) {
    return { enabled: flagOn || param || sticky, stick: param && !sticky };
}

/** `settled`: whether `enabled` is final — `false` only while Unleash hasn't
 *  answered and nothing else turned the tutorial on. */
export function useTutorialGate() {
    const flagOn = useFlag(TUTORIAL_FLAG);
    const { flagsReady, flagsError } = useFlagsStatus();
    const { search } = useLocation();
    const [sticky, setSticky] = useAtom(tutorialOverrideAtom);
    const { enabled, stick } = decideTutorialGate({ flagOn, param: hasTutorialParam(search), sticky });

    useEffect(() => {
        if (stick) setSticky(true);
    }, [stick, setSticky]);

    return { enabled, settled: enabled || flagsReady || Boolean(flagsError) };
}

export const useTutorialEnabled = () => useTutorialGate().enabled;
